/**
 * Feature E02-F02 — "Детальная карточка проекта + степпер" shared contracts.
 *
 * Owns the wire shapes for the project DETAIL CARD (richer than the post-mutation
 * `projectDetailResultSchema` in projectMutations.ts): it adds the `emails`
 * collection and `source_email_id`, and pins the exact fields the card reads.
 *
 * 1:1 port of the legacy `get_1_project` action (docs/source-n8n/sql-queries.md →
 * "DB: Get 1 Project + all data") and the legacy front-end card/stepper logic
 * (EER app/public/assets/js/project.js → renderProjectHeader / updateStepper /
 * setProjectStageView / renderClientAutocomplete / saveContactEdit). The Wire
 * pass re-exports these from packages/shared/src/index.ts.
 *
 * Why a separate detail schema (not reusing projectDetailResultSchema):
 *   - the legacy `DB: Get 1 Project + all data` SELECT returns THREE collections
 *     (artifacts, tasks, events) plus a scalar `source_email_id`. The `emails`
 *     collection is a NEW addition for the card (the legacy query does not return
 *     it) — together with `source_email_id` the card carries the full set the
 *     header/stepper/email panel need, where the F03 post-mutation read carried
 *     only the three legacy collections.
 *   - the card never receives a full email `body` — it gets `body_preview`
 *     (LEFT(body, 500)) so a long thread cannot bloat the card payload.
 */
import { z } from 'zod';
import { projectSourcingTeamRefSchema } from './project-sourcing-team.js';
// ---------------------------------------------------------------------------
// Collections embedded in the detail payload
// ---------------------------------------------------------------------------
/** One artifact row (project_artifacts projection from the LATERAL agg). */
export const detailArtifactSchema = z
    .object({
    id: z.number().int(),
    project_id: z.number().int(),
    type: z.string(),
    status: z.string().nullable(),
    source: z.string().nullable(),
    content: z.unknown(),
    file_url: z.string().nullable(),
    updated_at: z.string(),
})
    .passthrough();
/** One kick-off task row (project_tasks, ORDER BY position ASC). */
export const detailTaskSchema = z
    .object({
    id: z.number().int(),
    project_id: z.number().int(),
    type: z.string().nullable(),
    text: z.string(),
    status: z.string().nullable(),
    position: z.number().int(),
    meta: z.unknown(),
    created_at: z.string(),
})
    .passthrough();
/** One project event (project_events, ORDER BY created_at DESC, LIMIT 50). */
export const detailEventSchema = z
    .object({
    id: z.number().int(),
    project_id: z.number().int(),
    event_type: z.string(),
    payload: z.unknown(),
    created_at: z.string(),
})
    .passthrough();
/**
 * One linked email — `body_preview` only (LEFT(body,500)), never the full body
 * (parity invariant: the card payload must not carry whole threads).
 */
export const detailEmailSchema = z
    .object({
    id: z.number().int(),
    project_id: z.number().int().nullable(),
    sender: z.string().nullable(),
    sender_name: z.string().nullable(),
    subject: z.string().nullable(),
    body_preview: z.string().nullable(),
    direction: z.string().nullable(),
    received_at: z.string().nullable(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// Project core (the card header reads these)
// ---------------------------------------------------------------------------
/**
 * The project object inside the detail envelope. Permissive (`passthrough`) so
 * any extra `p.*` columns survive, but it PINS the fields the card depends on:
 * `client_name` resolves through COALESCE(project.client_id, contact.client_id) —
 * the project's OWN client wins, the contact's is only a fallback for legacy rows
 * with no project client (ADR PK-QA-9); `contact_*` come from the contacts JOIN;
 * `data` is always an object ({} when null); `contact_id`/`owner_id`/`status` are
 * nullable.
 */
export const detailProjectSchema = z
    .object({
    id: z.number().int(),
    role_name: z.string().nullable(),
    client_id: z.number().int().nullable(),
    contact_id: z.number().int().nullable(),
    owner_id: z.string().nullable(),
    stage: z.number().int().nullable(),
    status: z.string().nullable(),
    google_folder_id: z.string().nullable(),
    position_type_id: z.number().int().nullable(),
    source_email_id: z.number().int().nullable(),
    client_name: z.string().nullable(),
    /**
     * PK-QA-16 — the resolved Client's human-entered website (`Client.website`),
     * powering the KickoffView "Search Company Website" Google site:-lookup and
     * the PreKickoff company-block editor. Nullable/optional: a client with no
     * website (or an older payload not yet carrying it) simply has no value — a
     * strictly additive contract change. See decision-log.md 2026-07-21.
     */
    client_website: z.string().nullable().optional(),
    contact_name: z.string().nullable(),
    contact_email: z.string().nullable(),
    data: z.record(z.string(), z.unknown()),
    created_at: z.string(),
    updated_at: z.string(),
    /**
     * E03-F02-T06 (backend half) — WHEN the Pre-Kickoff AI analysis last
     * completed and WHO triggered it (`ai_analysis_completed` ProjectEvent,
     * `createdAt`/`createdBy`), so the frontend can show "last analyzed: <date>
     * · <actor>". Nullable/optional: a project that was never analyzed (or any
     * older client not yet on this field) simply has no value — this is a
     * strictly additive contract change.
     */
    last_analysis_at: z.string().nullable().optional(),
    last_analysis_by: z.string().nullable().optional(),
    /**
     * E03-F08-T01 (FP-05) — the PERSISTED sourcing-team assignment
     * (`projects.sourcing_team_id`). `sourcing_team_id` is the stored FK (null =
     * unassigned); `sourcing_team` is the resolved team (id/name/members) for
     * convenient rendering. Both optional/nullable: a project with no team, or an
     * older client not yet on this field, simply has no value — a strictly
     * additive contract change.
     */
    sourcing_team_id: z.number().int().nullable().optional(),
    sourcing_team: projectSourcingTeamRefSchema.nullable().optional(),
    /**
     * E03-F07-T07 — WHEN the «Kickoff done mail» (item_key `internal_notification`)
     * was last sent: the latest `internal_notification_sent` ProjectEvent's
     * `createdAt`. The send usually has no artifact row to flip to `sent` (the
     * template path never creates one), so the event is the only durable record.
     * Nullable/optional — never sent, or an older client: strictly additive.
     */
    internal_notification_sent_at: z.string().nullable().optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// GET /projects/:id envelope (get_1_project)
// ---------------------------------------------------------------------------
/**
 * Successful card payload: `{ success:true, project, artifacts, tasks, events,
 * emails }`. The four collections default to `[]` (COALESCE parity). On a
 * missing project the route returns the failure envelope instead.
 */
export const projectDetailCardSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    project: detailProjectSchema,
    artifacts: z.array(detailArtifactSchema).default([]),
    tasks: z.array(detailTaskSchema).default([]),
    events: z.array(detailEventSchema).default([]),
    emails: z.array(detailEmailSchema).default([]),
});
/**
 * Failure envelope when the id does not exist / is out of scope (legacy SPA:
 * `{ success:false, error }` — `dashboard.js → getProject` branches on it).
 */
export const projectDetailCardFailureSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
/** Either branch of the card response. */
export const projectDetailCardResponseSchema = z.union([
    projectDetailCardSchema,
    projectDetailCardFailureSchema,
]);
// ---------------------------------------------------------------------------
// Header inline-edit unsaved-updates contract (T03/T05/T06)
// ---------------------------------------------------------------------------
/**
 * The `{first_name,last_name,email}` triple the contact editor collects
 * (saveContactEdit). Assembled into a single contact name server-side.
 */
export const contactEditSchema = z.object({
    first_name: z.string().default(''),
    last_name: z.string().default(''),
    email: z.string().default(''),
});
/** A client autocomplete option (state.clients item). */
export const clientOptionSchema = z.object({
    id: z.number().int(),
    name: z.string(),
});
//# sourceMappingURL=project-detail-card.js.map